import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
  useTheme,
  Badge,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Capacitor } from '@capacitor/core';
import { BOTTOM_NAV_HEIGHT } from '@/mobile/constants/mobile';
import { useAuthStore } from '@/store/authStore';
import { useSuperAdminAuthStore } from '@/store/superAdminAuthStore';
import { useNotificationUnreadCount } from '@/modules/notifications/hooks/useNotifications';
import { useTranslation } from '@/i18n';

import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';

interface MobileNavItem {
  translationKey: string;
  shortLabel?: string;
  path: string;
  icon: React.ReactNode;
  isAlert?: boolean;
}

const RESIDENT_NAV_ITEMS: readonly MobileNavItem[] = [
  { translationKey: 'common.home', shortLabel: 'Home', path: '/dashboard', icon: <HomeOutlinedIcon /> },
  { translationKey: 'nav.financePayments', shortLabel: 'Payments', path: '/dues', icon: <CurrencyRupeeIcon /> },
  { translationKey: 'nav.visitors', shortLabel: 'Visitors', path: '/visitors', icon: <ShieldOutlinedIcon /> },
  { translationKey: 'nav.complaints', shortLabel: 'Complaints', path: '/complaints', icon: <SupportAgentOutlinedIcon /> },
  { translationKey: 'common.more', shortLabel: 'More', path: '/profile', icon: <MoreHorizOutlinedIcon /> },
];

const SUPER_ADMIN_NAV_ITEMS: readonly MobileNavItem[] = [
  { translationKey: 'nav.dashboard', shortLabel: 'Dashboard', path: '/super-admin', icon: <DashboardOutlinedIcon /> },
  { translationKey: 'nav.platformSocieties', shortLabel: 'Societies', path: '/super-admin/societies', icon: <ApartmentOutlinedIcon /> },
  { translationKey: 'nav.platformPlans', shortLabel: 'Plans', path: '/super-admin/plans', icon: <ReceiptLongOutlinedIcon /> },
  { translationKey: 'nav.platformSystemHealth', shortLabel: 'Health', path: '/super-admin/system-health', icon: <HealthAndSafetyOutlinedIcon /> },
  { translationKey: 'nav.platformNotifications', shortLabel: 'Alerts', path: '/super-admin/notifications', icon: <NotificationsNoneOutlinedIcon />, isAlert: true },
];



function pathMatches(current: string, target: string): boolean {
  if (target === '/super-admin') {
    return current === '/super-admin' || current === '/super-admin/' || current === '/super-admin/dashboard';
  }
  if (target === '/super-admin/societies') {
    return current.startsWith('/super-admin/societies');
  }
  if (target === '/super-admin/plans') {
    return (
      current.startsWith('/super-admin/plans') ||
      current.startsWith('/super-admin/pricing-overrides') ||
      current.startsWith('/super-admin/marketing')
    );
  }
  if (target === '/super-admin/system-health') {
    return (
      current.startsWith('/super-admin/system-health') ||
      current.startsWith('/super-admin/diagnostics') ||
      current.startsWith('/super-admin/logs')
    );
  }
  if (target === '/super-admin/notifications' || target === '/notifications') {
    return current.startsWith('/notifications') || current.startsWith('/super-admin/notifications');
  }
  if (target === '/complaints') {
    return current === '/complaints' || current.startsWith('/complaints/');
  }
  if (target === '/dues') {
    return current === '/dues' || current.startsWith('/dues') || current.startsWith('/collections') || current.startsWith('/finance');
  }
  if (target === '/visitors') {
    return (
      current === '/visitors' ||
      current.startsWith('/visitors') ||
      current.startsWith('/gate-passes') ||
      current.startsWith('/security') ||
      current.startsWith('/cctv') ||
      current.startsWith('/cameras') ||
      current.startsWith('/watchman') ||
      current.startsWith('/gates') ||
      current.startsWith('/shifts')
    );
  }
  if (target === '/profile') {
    return current === '/profile' || current.startsWith('/settings');
  }
  if (target === '/dashboard' || target === '/m/home') {
    return current === '/dashboard' || current === '/' || current === '/m/home';
  }
  return current === target || current.startsWith(`${target}/`);
}

export function shouldShowMobileBottomNav(isXs: boolean): boolean {
  return Capacitor.isNativePlatform() || isXs;
}

export function MobileBottomNav() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const visible = shouldShowMobileBottomNav(isXs);

  const { isSuperAdmin, user } = useAuthStore();
  const { isAuthenticated: isPlatformAuth } = useSuperAdminAuthStore();
  const unreadQuery = useNotificationUnreadCount();

  const isSuperAdminUser = useMemo(() => {
    return isSuperAdmin() || isPlatformAuth || (user as any)?.role === 'SUPER_ADMIN' || (user as any)?.scope === 'PLATFORM';
  }, [isSuperAdmin, isPlatformAuth, user]);

  const navItems = isSuperAdminUser ? SUPER_ADMIN_NAV_ITEMS : RESIDENT_NAV_ITEMS;

  const value = useMemo(() => {
    const idx = navItems.findIndex((item) => pathMatches(location.pathname, item.path));
    return idx >= 0 ? idx : 0;
  }, [location.pathname, navItems]);

  const { t } = useTranslation();

  if (!visible) return null;

  const unreadCount = unreadQuery.data?.unread ?? unreadQuery.data?.count ?? 0;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar,
        borderRadius: 0,
        pb: 'env(safe-area-inset-bottom)',
        bgcolor: (t) => t.palette.background.paper,
        backdropFilter: 'blur(16px)',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <BottomNavigation
        showLabels
        value={value}
        onChange={(_, next) => {
          const item = navItems[next as number];
          if (item) navigate(item.path);
        }}
        sx={{
          height: BOTTOM_NAV_HEIGHT,
          display: 'flex',
          justifyContent: 'space-around',
          bgcolor: 'transparent',
          '& .MuiBottomNavigationAction-root': {
            flex: 1,
            minWidth: 0,
            maxWidth: 'none',
            padding: '6px 2px',
            color: '#4F5F78',
            transition: 'color 150ms ease',
            WebkitTapHighlightColor: 'transparent',
            '& .MuiTouchRipple-root': {
              display: 'none !important',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.45rem',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.72rem !important',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              mt: 0.25,
              '&.Mui-selected': {
                fontSize: '0.72rem !important',
                fontWeight: 700,
              },
            },
            '&.Mui-selected': {
              color: '#00B894',
              '& .MuiSvgIcon-root': {
                color: '#00B894',
              },
            },
          },
        }}
      >
        {navItems.map((item) => {
          const itemLabel = 'shortLabel' in item && item.shortLabel ? item.shortLabel : t(item.translationKey);
          const iconWithBadge =
            'isAlert' in item && item.isAlert && unreadCount > 0 ? (
              <Badge
                badgeContent={unreadCount}
                color="error"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    height: 16,
                    minWidth: 16,
                    padding: '0 3px',
                    borderRadius: '8px',
                    bgcolor: '#FF5757',
                    top: 2,
                    right: -2,
                  },
                }}
              >
                {item.icon}
              </Badge>
            ) : (
              item.icon
            );

          return (
            <BottomNavigationAction
              key={item.path}
              label={itemLabel}
              icon={iconWithBadge}
              disableRipple
            />
          );
        })}

      </BottomNavigation>
    </Paper>
  );
}

export { BOTTOM_NAV_HEIGHT };
